import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { EASE, Reveal } from "@/components/site/motion";

const NARRATIVE = [
  "My path into partnerships started long before I had the title.",
  "I began as a customer service rep at a trucking company and was promoted to Operations Manager within a year, then to Director of Product. What moved me forward was a habit that has followed me throughout my career: listening closely to customers, spotting where the business was leaving value on the table, and building something that could capture it.",
  "That led to new, high-margin service offerings, a customer-facing technology interface, and growth of the company’s value-added LTL business from $300K to $12–15M annually. It also helped move the business from a traditional trucking model toward a more technology-enabled logistics company. I was already working at the intersection of customers, technology, distribution, and external relationships. I just didn’t call it partnerships yet.",
  "That instinct carried into channel sales at NCR and account management at YourCause before it became the center of my career.",
  "At CoreLogic, my performance in relationship management led the company to create a new role for me: Senior Channel Partner Manager. It was part of a broader growth strategy for a mature company looking to expand into adjacent markets. I used the role to build the company’s first structured partner program from the ground up, growing partner-sourced revenue 50%, then 63%, in consecutive years.",
  "From there, the pattern became clear. At Clear Capital, I built a partner program from zero, managed a $200M portfolio, and generated a $10M pipeline in 60 days through a single channel partner. At StarRez, I reworked commercial agreements with tier-one partners and increased partner-program profitability 150%. Most recently at Community Tax, I reversed a declining top-partner relationship into positive growth and helped grow overall program performance 30% year over year.",
  "The environments changed. The work didn’t.",
  "I’m at my best when a partner program has potential but lacks structure, focus, or commercial momentum — or when a company needs new markets and partner routes to expand TAM. I assess what is actually happening in the data and across the business, identify where the real opportunity sits, build the operating model around it, and get hands-on with the partners that matter most. Strategy matters, but only if it turns into activation, pipeline, revenue, and measurable business value.",
  "That approach has transferred across fintech, proptech, payments, higher education, tax, logistics, and enterprise technology because the fundamentals are remarkably consistent: prioritize the right partners, create a value exchange both sides will invest in, build the infrastructure to support execution, and measure what produces growth.",
  "I also bring a PMP mindset to the work. Governance, KPIs, operating cadence, accountability, and execution discipline are part of how I run a program, not administrative extras. That combination of commercial instinct and operational rigor is what allows me to step into a new or underperforming partnership function, understand it quickly, and start moving it forward.",
];

const HIGHLIGHTS = [
  {
    org: "Community Tax",
    when: "Jul 2024 – Sept 2026",
    text: "Director of Channel Marketing & Partnerships. Reversed a declining top-partner relationship into 33% positive growth and built the executive scorecard program.",
  },
  {
    org: "StarRez / Vista Equity Partners",
    when: "Apr – Oct 2023",
    text: "Director of Strategic Partnerships & Partner Program. Renegotiated partner agreements to increase program profitability 150%, architected a payments monetization strategy, and led a joint GTM with Microsoft and Amazon for 1,300 customers.",
  },
  {
    org: "Clear Capital",
    when: "Sept 2020 – Jan 2023",
    text: "Senior Partner Channel Manager. Built the partner program from the ground up, managed a $200M portfolio, and generated a $10M pipeline in 60 days through a single channel partner.",
  },
  {
    org: "CoreLogic",
    when: "Nov 2018 – Sept 2020",
    text: "Senior Channel Partner Manager, a role created based on my prior performance. Built the company’s first structured partner program, growing partner-sourced revenue 50%, then 63%.",
  },
  {
    org: "CoreLogic",
    when: "Oct 2017 – Nov 2018",
    text: "Senior Relationship Manager. Won the company’s Pinnacle Award for leading a 250-client platform migration with zero disruption to clients.",
  },
  {
    org: "YourCause",
    when: "Sept 2014 – May 2017",
    text: "Senior Account Manager. Coached a 10-person client services team to a 98% satisfaction score and grew revenue across 89% of the account portfolio.",
  },
];

export default function About() {
  return (
    <>
      <section className="page-hero" data-testid="about-hero">
        <div className="wrap">
          <motion.span
            className="kicker"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            About
          </motion.span>
          <h1 style={{ fontSize: "clamp(2rem, 3.6vw, 2.9rem)" }} data-testid="about-heading">
            <span className="mask-line">
              <motion.span
                className="mask-inner"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
              >
                15+ years. Different markets. One repeatable partnership playbook.
              </motion.span>
            </span>
          </h1>
          <motion.p
            className="lede"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
          >
            Different markets, one repeatable approach: turning partnerships into measurable revenue, not just
            signed deals.
          </motion.p>
        </div>
      </section>

      <section data-testid="about-body">
        <div className="wrap about-layout">
          <Reveal className="about-portrait" data-testid="about-portrait">
            <div className="portrait-frame">
              <img src="/assets/rhonda-johnson-headshot.jpg" alt="Portrait of Rhonda Johnson" data-testid="about-headshot" />
            </div>
            <div className="role-note" data-testid="about-role-note">
              <p><b>Rhonda Johnson</b></p>
              <p>Frisco, TX</p>
              <p>PMP, Project Management Institute</p>
              <p>
                <a href="https://linkedin.com/in/rjohnson3" target="_blank" rel="noopener noreferrer" data-testid="about-linkedin-link">
                  linkedin.com/in/rjohnson3
                </a>
              </p>
            </div>
          </Reveal>

          <div className="about-copy" data-testid="about-narrative">
            {NARRATIVE.map((para, i) => (
              <Reveal key={i} y={22} delay={i * 0.04}>
                <p>{para}</p>
              </Reveal>
            ))}
            <Reveal y={18}>
              <Link to="/contact" className="btn btn-primary" data-testid="about-mid-cta" style={{ marginTop: "0.6rem" }}>
                Start a Conversation <span className="btn-arrow">→</span>
              </Link>
            </Reveal>

            <div className="highlight-list" data-testid="career-highlights">
              <Reveal y={20}>
                <span className="kicker">
                  <span className="chapter">Ledger</span>
                  Career highlights
                </span>
              </Reveal>
              {HIGHLIGHTS.map((h, i) => (
                <Reveal key={`${h.org}-${h.when}`} y={22} delay={(i % 3) * 0.05} className="highlight-row" data-testid={`highlight-row-${i}`}>
                  <div className="h-org">
                    {h.org}
                    <small>{h.when}</small>
                  </div>
                  <p>{h.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="cta-band" data-testid="about-cta-section">
        <div className="wrap">
          <Reveal y={26}>
            <span className="kicker" style={{ color: "var(--brass-bright)" }}>
              <span className="chapter" style={{ color: "var(--brass)" }}>Next step</span>
              Start the conversation
            </span>
            <h2>Let’s talk about what your partnerships could be producing.</h2>
            <p>
              Bring the problem you’re facing, whatever stage it’s at, and we’ll work out whether fractional leadership
              is the right next step.
            </p>
            <div className="btn-row">
              <Link to="/contact" className="btn btn-on-ink" data-testid="about-cta-primary">
                Discuss a Partnership Challenge <span className="btn-arrow">→</span>
              </Link>
              <a href="mailto:info@rhondajohnson.co" className="btn btn-ghost-on-ink" data-testid="about-cta-email">
                Email Rhonda Directly
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
